import { format, isPast, isSameMonth, startOfMonth } from 'date-fns';
import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../contexts/GlobalContext';

const TimelineProgressStats = ({ questions, currentApps }) => {
  const [months, setMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { questPlan } = useContext(GlobalContext);

  useEffect(() => {
    if (!questPlan?.quest_plan) {
      setIsLoading(false);
      return;
    }

    const processMonths = () => {
      const monthMap = new Map();

      questPlan.quest_plan.forEach((week) => {
        const monthStart = startOfMonth(new Date(week.week_start));
        const monthKey = monthStart.toISOString();

        if (!monthMap.has(monthKey)) {
          monthMap.set(monthKey, {
            monthStart,
            leetcodeGoal: 0,
            appGoal: 0,
            tasks: {
              leetcode: [],
              apps: [],
              lessons: [],
              resume: [],
            },
          });
        }

        const monthData = monthMap.get(monthKey);
        monthData.leetcodeGoal += week.tasks.leetcode.length;
        monthData.appGoal += week.tasks.apps.length;
        monthData.tasks.leetcode.push(...week.tasks.leetcode);
        monthData.tasks.apps.push(...week.tasks.apps);
        monthData.tasks.lessons.push(...week.tasks.lessons);
        monthData.tasks.resume.push(...week.tasks.resume);
      });

      return Array.from(monthMap.values()).sort((a, b) => a.monthStart - b.monthStart);
    };

    setMonths(processMonths());
    setIsLoading(false);
  }, [questPlan]);

  const countCompletedTasks = (month) => {
    const monthStart = startOfMonth(month.monthStart);
    const monthEnd = new Date(monthStart);
    monthEnd.setMonth(monthEnd.getMonth() + 1);

    // Count completed tasks directly from questPlan data
    const leetcodeCompleted = month.tasks.leetcode.filter((task) => task.completed).length;
    const applicationsSubmitted = month.tasks.apps.filter((task) => task.completed).length;
    const lessonsCompleted = month.tasks.lessons.filter((task) => task.completed).length;
    const resumeTasksCompleted = month.tasks.resume.filter((task) => task.completed).length;

    return {
      leetcodeCompleted,
      applicationsSubmitted,
      lessonsCompleted,
      resumeTasksCompleted,
    };
  };

  const ProgressBar = ({ completed, total, color }) => {
    const percentage = total > 0 ? (completed / total) * 100 : 0;
    return (
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md h-full flex items-center justify-center">
        <div className="text-gray-500">Loading timeline...</div>
      </div>
    );
  }

  if (!questPlan?.quest_plan || questPlan.quest_plan.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 mb-2">No quest plan available</div>
          <div className="text-sm text-gray-400">Start a new quest to see your timeline</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-full overflow-hidden flex flex-col">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Quest Timeline</h2>
      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="space-y-6">
          {months.map((month, index) => {
            const {
              leetcodeCompleted,
              applicationsSubmitted,
              lessonsCompleted,
              resumeTasksCompleted,
            } = countCompletedTasks(month);

            const isCurrentMonth = isSameMonth(month.monthStart, new Date());
            const isPastMonth = isPast(month.monthStart);
            const monthName = format(month.monthStart, 'MMMM yyyy');

            const getMonthColor = () => {
              if (isCurrentMonth) return 'bg-blue-50 border-blue-200';
              if (isPastMonth) return 'bg-green-50 border-green-200';
              return 'bg-gray-50 border-gray-200';
            };

            return (
              <div key={index} className="relative">
                <div className={`p-3 rounded-lg border ${getMonthColor()}`}>
                  <div className="text-sm font-medium text-gray-700 mb-3">{monthName}</div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <div className="text-xs text-gray-600">LeetCode</div>
                        <div className="text-xs text-gray-500">
                          {leetcodeCompleted}/{month.leetcodeGoal}
                        </div>
                      </div>
                      <ProgressBar
                        completed={leetcodeCompleted}
                        total={month.leetcodeGoal}
                        color="bg-blue-500"
                      />
                    </div>
                    {month.appGoal > 0 && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-xs text-gray-600">Applications</div>
                          <div className="text-xs text-gray-500">
                            {applicationsSubmitted}/{month.appGoal}
                          </div>
                        </div>
                        <ProgressBar
                          completed={applicationsSubmitted}
                          total={month.appGoal}
                          color="bg-purple-500"
                        />
                      </div>
                    )}
                    {month.tasks.lessons.length > 0 && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-xs text-gray-600">Lessons</div>
                          <div className="text-xs text-gray-500">
                            {lessonsCompleted}/{month.tasks.lessons.length}
                          </div>
                        </div>
                        <ProgressBar
                          completed={lessonsCompleted}
                          total={month.tasks.lessons.length}
                          color="bg-orange-500"
                        />
                      </div>
                    )}
                    {month.tasks.resume.length > 0 && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-xs text-gray-600">Resume Tasks</div>
                          <div className="text-xs text-gray-500">
                            {resumeTasksCompleted}/{month.tasks.resume.length}
                          </div>
                        </div>
                        <ProgressBar
                          completed={resumeTasksCompleted}
                          total={month.tasks.resume.length}
                          color="bg-green-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <style jsx>{`
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default TimelineProgressStats;
